import cx from "classnames";
import { type FC, type ReactNode, useMemo } from "react";

import styles from "./_.module.css";

import { useTheme } from "../ThemeProvider";

export interface LoadingSkeletonProps {
	showChildren?: boolean;
	barCount?: number;
	SkeletonWrapper?: FC<any> | null;
	randomizeWidths?: boolean;
	defaultWidth?: string | number;
	baseKey?: string;
	children?: ReactNode;
}

export const LoadingSkeleton: FC<LoadingSkeletonProps> = ({
	barCount = 1,
	randomizeWidths = false,
	defaultWidth = "100%",
	baseKey = "loading-skeleton",
	SkeletonWrapper,
	showChildren = false,
	children,
}) => {
	const { resolvedTheme } = useTheme();
	const values = useMemo(
		() =>
			new Array(Math.max(1, barCount))
				.fill("")
				.map(() =>
					randomizeWidths ? String(Math.random() * 70 + 30) + "%" : defaultWidth
				),
		[]
	);

	const Noop: FC<{ children: ReactNode }> = ({ children }) => <>{children}</>;
	const Wrapper: FC<any> = SkeletonWrapper || Noop;

	return (
		<>
			{!showChildren && (
				<Wrapper>
					{values.map((e, i) => (
						<div
							style={{ width: e }}
							className={cx(styles["skeleton-loader"], `h-6 rounded-md`, {
								// One of the extremely rare times we should switch on theme. People
								// are conditioned to view loading skeletons as "gray", where "gray"
								// means "an unsaturated color light relative to the background."
								"bg-tertiary": resolvedTheme !== "dark",
								"bg-primary-raised": resolvedTheme === "dark",
							})}
							key={baseKey + i}
						/>
					))}
				</Wrapper>
			)}
			{/*
                child nodes are always mounted to the DOM tree,
                but are set to "display:none" if skeleton view is active
                this way it doesn't interrupt any of the canvas runtime stuff
            */}
			<div style={{ display: showChildren ? "unset" : "none" }}>{children}</div>
		</>
	);
};
